/**
 * Registre d'imagerie ASCEND V7 — résout une clé logique (nom de fichier canonique) vers l'URL
 * gérée par Vite. Aucun chemin public implicite, aucun CDN : les 32 images sont importées
 * statiquement depuis `presentation/styles/ascend/assets/imagery/`, déjà intégrées au dépôt.
 *
 * Séparé de `modes.ts`/`emotional-impact.ts` : ces fichiers décrivent la présentation par clé
 * logique, celui-ci est seul responsable de la résolution technique de l'asset (une
 * responsabilité, un fichier).
 */

const modules = import.meta.glob<string>('../../styles/ascend/assets/imagery/*.png', {
  eager: true,
  import: 'default',
});

const IMAGERY: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(modules).map(([path, url]) => [path.split('/').pop() ?? path, url]),
);

export type ImageKey =
  | 'atmosphere-discipline.png'
  | 'atmosphere-elevation.png'
  | 'atmosphere-mouvement.png'
  | 'atmosphere-reparation.png'
  | 'card-corps.png'
  | 'card-guidance.png'
  | 'card-stoic.png'
  | 'family-coup-dur.png'
  | 'family-friction.png'
  | 'family-pression-peur.png'
  | 'family-rayonnement.png'
  | 'family-recentrage.png'
  | 'family-reparation.png'
  | 'family-serenite.png'
  | 'family-vulnerabilite.png'
  | 'hero-charge.png'
  | 'hero-coup-dur.png'
  | 'hero-difficile.png'
  | 'hero-elevation.png'
  | 'hero-imprevu.png'
  | 'hero-moment-decisif.png'
  | 'hero-normal.png'
  | 'hero-recuperation.png'
  | 'hero-reparation.png'
  | 'hero-transition.png'
  | 'hero-voyage.png'
  | 'visual-guidance.png'
  | 'visual-maitrise.png'
  | 'visual-mouvement.png'
  | 'visual-orientation.png'
  | 'visual-reparation.png'
  | 'visual-stoicien.png'
  // — Pack V2 (1870×841, revue visuelle V7 #3) — les 32 clés ci-dessus restent résolues pour
  // permettre un rollback ; seul le mapping runtime (modes.ts/emotional-impact.ts/cartes
  // éditoriales) bascule sur les clés -v2 ci-dessous.
  | 'hero-normal-v2.png'
  | 'hero-charge-v2.png'
  | 'hero-voyage-v2.png'
  | 'hero-recuperation-v2.png'
  | 'hero-difficile-v2.png'
  | 'hero-elevation-v2.png'
  | 'hero-moment-decisif-v2.png'
  | 'hero-imprevu-v2.png'
  | 'hero-transition-v2.png'
  | 'card-sport-v2.png'
  | 'card-inspiration-v2.png'
  | 'card-guidance-v2.png'
  | 'family-rayonnement-v2.png'
  | 'family-serenite-v2.png'
  | 'family-pression-peur-v2.png'
  | 'family-friction-v2.png'
  | 'family-vulnerabilite-v2.png'
  | 'family-reparation-v2.png'
  | 'family-recentrage-v2.png'
  | 'family-coup-dur-v2.png';

/** Fallback sûr si une clé venait à manquer — toujours une image réelle du pack. */
export const FALLBACK_IMAGE_KEY: ImageKey = 'hero-normal-v2.png';

/** Résout une clé canonique vers son URL Vite. Retombe sur l'image de secours si absente. */
export function resolveImagery(key: ImageKey): string {
  return IMAGERY[key] ?? IMAGERY[FALLBACK_IMAGE_KEY] ?? '';
}

/** Vérifie qu'une clé résout bien vers un asset réel du pack (utilisé par les tests d'intégrité). */
export function hasImagery(key: string): boolean {
  return key in IMAGERY;
}

export function listImageryKeys(): readonly string[] {
  return Object.keys(IMAGERY);
}
